/**
 * Customer bounded context — customer model v2 (TO-418). Every value set is a
 * TypeScript enum persisted as TEXT (never a Postgres ENUM).
 */

/** Lead vs customer. The transition is one-way: lead → customer. */
export enum CustomerType {
	LEAD = 'lead',
	CUSTOMER = 'customer',
}

/** Business status. Deletion is `deletedAt`, never a status. */
export enum CustomerStatus {
	ACTIVE = 'active',
	INACTIVE = 'inactive',
}

/** The channel the customer prefers to be contacted on. */
export enum CustomerChannel {
	EMAIL = 'email',
	WHATSAPP = 'whatsapp',
	SMS = 'sms',
	PHONE = 'phone',
	IN_STORE = 'in_store',
}

/** Where a customer (or one of its child records) came from. */
export enum CustomerSource {
	FILE_IMPORT = 'file_import',
	ADMIN_UI = 'admin_ui',
	API = 'api',
	/** A STOP/START reply to an SMS, captured from Twilio (TO-474). */
	TWILIO_STOP = 'twilio_stop',
	/** A prospect created inline in the offer wizard's recipient step (TO-122). */
	OFFER_WIZARD = 'offer_wizard',
	/** A one-click `List-Unsubscribe` request from a marketing email (TO-138). */
	UNSUBSCRIBE_EMAIL = 'unsubscribe_email',
	/** A spam complaint reported through SES feedback (TO-136); withdraws email marketing consent. */
	SES_COMPLAINT = 'ses_complaint',
}

/**
 * Whether a customer can be emailed, maintained from SES feedback (TO-136).
 * `HARD_BOUNCE` / `COMPLAINED` suppress every email; `SOFT_BOUNCE` suppresses
 * once {@link CUSTOMER_EMAIL_SOFT_BOUNCE_THRESHOLD} transient bounces accumulate.
 */
export enum CustomerEmailState {
	VALID = 'valid',
	SOFT_BOUNCE = 'soft_bounce',
	HARD_BOUNCE = 'hard_bounce',
	COMPLAINED = 'complained',
}

/** Transient bounces (on distinct notifications, no delivery since) after which email is suppressed. */
export const CUSTOMER_EMAIL_SOFT_BOUNCE_THRESHOLD = 3

/** Which path recorded a conversion (TO-405): a file import or the push API (TO-76). */
export enum ConversionSource {
	FILE = 'file',
	API = 'api',
}

/**
 * Why a conversion cannot be recorded (TO-405 BR-4). Shared by the file
 * importer (as plan rejection / entry failure codes) and the push API (TO-76).
 */
export enum ConversionErrorCode {
	CONVERSION_APPEND_ONLY = 'CONVERSION_APPEND_ONLY',
	CUSTOMER_NOT_FOUND = 'CUSTOMER_NOT_FOUND',
	INVALID_CONTRACTED_AT = 'INVALID_CONTRACTED_AT',
	INVALID_CONTRACT_AMOUNT = 'INVALID_CONTRACT_AMOUNT',
	INVALID_CONTRACT_CURRENCY = 'INVALID_CONTRACT_CURRENCY',
	INVALID_OFFER_ID = 'INVALID_OFFER_ID',
	CONVERSION_SOURCE_MISSING = 'CONVERSION_SOURCE_MISSING',
}

/**
 * Why the `client@1` importer refuses a record (TO-403 BR-7), as plan rejection
 * and entry failure codes. Listed in check precedence order.
 */
export enum ClientImportErrorCode {
	CLIENT_STRATEGY_UNSUPPORTED = 'CLIENT_STRATEGY_UNSUPPORTED',
	CUSTOMER_ANONYMIZED = 'CUSTOMER_ANONYMIZED',
	INVALID_FIRST_NAME = 'INVALID_FIRST_NAME',
	INVALID_TYPE = 'INVALID_TYPE',
	INVALID_PREFERRED_CHANNEL = 'INVALID_PREFERRED_CHANNEL',
	INVALID_PREFERRED_LOCALE = 'INVALID_PREFERRED_LOCALE',
	INVALID_EMAIL = 'INVALID_EMAIL',
	INVALID_MOBILE = 'INVALID_MOBILE',
	TYPE_DOWNGRADE_FORBIDDEN = 'TYPE_DOWNGRADE_FORBIDDEN',
	ORG_UNIT_UNKNOWN = 'ORG_UNIT_UNKNOWN',
	ORG_UNIT_INACTIVE = 'ORG_UNIT_INACTIVE',
	ASSIGNED_USER_UNKNOWN = 'ASSIGNED_USER_UNKNOWN',
	ASSIGNED_USER_UNAVAILABLE = 'ASSIGNED_USER_UNAVAILABLE',
	PREFERRED_CHANNEL_UNREACHABLE = 'PREFERRED_CHANNEL_UNREACHABLE',
	CONTACT_POINT_LIMIT = 'CONTACT_POINT_LIMIT',
}

export enum ContactPointType {
	EMAIL = 'email',
	MOBILE = 'mobile',
	LANDLINE = 'landline',
}

export enum ContactPointLabel {
	PERSONAL = 'personal',
	WORK = 'work',
	OTHER = 'other',
}

/** Why an assignment period started. INITIAL and IMPORT_SYNC are server-assigned only. */
export enum AssignmentReason {
	INITIAL = 'initial',
	MANUAL_REASSIGN = 'manual_reassign',
	IMPORT_SYNC = 'import_sync',
	USER_OFFBOARDED = 'user_offboarded',
	UNIT_RESTRUCTURE = 'unit_restructure',
}

export enum ConsentChannel {
	EMAIL = 'email',
	SMS = 'sms',
	WHATSAPP = 'whatsapp',
	PHONE = 'phone',
	ANY = 'any',
}

export enum ConsentPurpose {
	MARKETING = 'marketing',
	PROFILING = 'profiling',
}

export enum ConsentStatus {
	GRANTED = 'granted',
	WITHDRAWN = 'withdrawn',
	PENDING_CONFIRMATION = 'pending_confirmation',
}

/**
 * Stable machine-readable error codes of the customer API. They prefix the
 * error message (`CODE: human text`), the platform's domain-error convention.
 */
export enum CustomerErrorCode {
	VALIDATION_FAILED = 'CUSTOMER_VALIDATION_FAILED',
	NOT_FOUND = 'CUSTOMER_NOT_FOUND',
	IMMUTABLE_FIELD = 'CUSTOMER_IMMUTABLE_FIELD',
	TYPE_DOWNGRADE_FORBIDDEN = 'CUSTOMER_TYPE_DOWNGRADE_FORBIDDEN',
	PREFERRED_CHANNEL_UNREACHABLE = 'CUSTOMER_PREFERRED_CHANNEL_UNREACHABLE',
	ORG_UNIT_INVALID = 'CUSTOMER_ORG_UNIT_INVALID',
	ORG_UNIT_OUT_OF_SCOPE = 'CUSTOMER_ORG_UNIT_OUT_OF_SCOPE',
	/** TO-388: create omitted `org_unit_id` and the caller has no single anchor unit to default to. */
	ORG_UNIT_REQUIRED = 'CUSTOMER_ORG_UNIT_REQUIRED',
	ASSIGNED_USER_INVALID = 'CUSTOMER_ASSIGNED_USER_INVALID',
	EXTERNAL_ID_TAKEN = 'CUSTOMER_EXTERNAL_ID_TAKEN',
	ANONYMIZED = 'CUSTOMER_ANONYMIZED',
	CONTACT_POINT_INVALID = 'CUSTOMER_CONTACT_POINT_INVALID',
	CONTACT_POINT_DUPLICATE = 'CUSTOMER_CONTACT_POINT_DUPLICATE',
	CONTACT_POINT_LIMIT = 'CUSTOMER_CONTACT_POINT_LIMIT',
	CONTACT_POINT_PRIMARY_REQUIRED = 'CUSTOMER_CONTACT_POINT_PRIMARY_REQUIRED',
	CONTACT_POINT_ORDER_MISMATCH = 'CUSTOMER_CONTACT_POINT_ORDER_MISMATCH',
	CONTACT_POINT_REQUIRED = 'CUSTOMER_CONTACT_POINT_REQUIRED',
	CONTACT_POINT_NOT_FOUND = 'CUSTOMER_CONTACT_POINT_NOT_FOUND',
	NOTE_NOT_AUTHOR = 'CUSTOMER_NOTE_NOT_AUTHOR',
	NOTE_NOT_FOUND = 'CUSTOMER_NOTE_NOT_FOUND',
	CONSENT_CONTACT_POINT_MISMATCH = 'CUSTOMER_CONSENT_CONTACT_POINT_MISMATCH',
	CONSENT_EFFECTIVE_AT_FUTURE = 'CUSTOMER_CONSENT_EFFECTIVE_AT_FUTURE',
	ATTRIBUTE_LIMIT = 'CUSTOMER_ATTRIBUTE_LIMIT',
	/**
	 * TO-138: the public unsubscribe link does not verify, or names no live customer.
	 * Deliberately unprefixed — the public route must not name the customer domain.
	 */
	UNSUBSCRIBE_LINK_INVALID = 'UNSUBSCRIBE_LINK_INVALID',
	/** TO-138: the public unsubscribe route's per-recipient or per-IP limit is reached. */
	UNSUBSCRIBE_RATE_LIMITED = 'UNSUBSCRIBE_RATE_LIMITED',
}

/**
 * Claims of a one-click unsubscribe token (TO-138 email-unsubscribe REQ-003):
 * the recipient, the channel it unsubscribes from, and the keyring version that
 * signed it. Tokens never expire.
 */
export interface UnsubscribeTokenClaims {
	tenantId: string
	customerId: string
	channel: ConsentChannel.EMAIL
	keyVersion: string
}

/** Locale used when a customer has no preferred locale (no tenant default exists yet). */
export const CUSTOMER_DEFAULT_LOCALE = 'es-ES'
/** Region used to parse national-format phone numbers (no tenant setting exists yet). */
export const CUSTOMER_DEFAULT_PHONE_REGION = 'ES'
/** Upper bound of live contact points per customer. */
export const CUSTOMER_MAX_CONTACT_POINTS = 20
/** Upper bound of attributes per customer. */
export const CUSTOMER_MAX_ATTRIBUTES = 200

/** A contact value as exposed in a DTO — masked unless the caller holds `customer.pii.read`. */
export interface ICustomerContactValue {
	contactPointId: string
	value: string
	masked: boolean
}

export interface ICustomer {
	/** Internal, app-owned id (primary key, globally unique). */
	customerId: string
	/** The tenant this customer belongs to. Every read/write is scoped to it. */
	tenantId: string
	/** Partner/CDP id; unique among the tenant's live customers. */
	externalId: string | null
	firstName: string
	lastName1: string | null
	lastName2: string | null
	/** Database-generated join of the non-empty name parts. */
	fullName: string
	type: CustomerType
	status: CustomerStatus
	statusPartnerLabel: string | null
	convertedAt: Date | null
	preferredChannel: CustomerChannel | null
	preferredLocale: string | null
	tags: string[]
	orgUnitId: string
	assignedUserId: string
	assignedAt: Date
	/** Email deliverability from SES feedback (TO-136); `VALID` until a bounce or complaint. */
	emailState: CustomerEmailState
	/** Transient bounces counted since the last delivery or reset (TO-136). */
	emailSoftBounceCount: number
	source: CustomerSource
	sourceRef: string | null
	createdBy: string
	updatedBy: string
	anonymizedAt: Date | null
	/** Derived soft-delete flag — `deletedAt` is the single source of truth. */
	isDeleted: boolean
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}

export interface ICustomerContactPoint {
	contactPointId: string
	tenantId: string
	customerId: string
	type: ContactPointType
	value: string
	valueNormalized: string
	label: ContactPointLabel
	priority: number
	isPrimary: boolean
	verifiedAt: Date | null
	invalidatedAt: Date | null
	source: CustomerSource
	sourceRef: string | null
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}

export interface ICustomerAssignment {
	assignmentId: string
	tenantId: string
	customerId: string
	orgUnitId: string
	assignedUserId: string
	validFrom: Date
	validTo: Date | null
	reason: AssignmentReason
	source: CustomerSource
	sourceRef: string | null
	assignedBy: string
}

export interface ICustomerNote {
	noteId: string
	tenantId: string
	customerId: string
	body: string
	authoredBy: string
	source: CustomerSource
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}

export interface ICustomerConsent {
	consentId: string
	tenantId: string
	customerId: string
	channel: ConsentChannel
	purpose: ConsentPurpose
	status: ConsentStatus
	contactPointId: string | null
	source: CustomerSource
	sourceRef: string | null
	capturedBy: string
	legalTextId: string | null
	legalTextVersion: string | null
	evidence: Record<string, unknown> | null
	effectiveAt: Date
	confirmedAt: Date | null
	createdAt: Date
}
